import { useParams, Navigate } from 'react-router-dom';
import { JobDetailTemplate } from '../JobDetailTemplate';
import type { JobData } from '../../types/job';
import { useJobs } from '../contexts/JobsContext';
import { isJobActive } from '../../types/job';

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { jobs, isLoading } = useJobs();
  
  // Show loading state while jobs are being fetched
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading job details...</p>
        </div>
      </div>
    );
  }
  
  // Find the job by id and convert to JobData format
  const rawJob = jobs.find(job => job.id === id);
  
  // Check if job exists, is active (status), and not expired
  if (!rawJob || rawJob.status !== 'active' || !isJobActive(rawJob)) {
    return <Navigate to="/careers" replace />;
  }
  
  const job: JobData = {
    id: rawJob.id,
    title: rawJob.title,
    department: rawJob.department,
    location: rawJob.location,
    workMode: rawJob.workMode || 'Remote',
    type: rawJob.type as 'Full-time' | 'Part-time' | 'Contract' | 'Internship',
    experience: rawJob.experience || 'Not specified',
    salary: rawJob.salary,
    postedDate: rawJob.postedDate || new Date().toISOString(),
    expirationDate: rawJob.expirationDate,
    description: rawJob.description,
    jobSummary: rawJob.jobSummary || rawJob.description,
    responsibilities: rawJob.responsibilities || [],
    requirements: rawJob.requirements || [],
    preferredQualifications: rawJob.preferredQualifications || [],
    benefits: rawJob.benefits || [],
    teamInfo: rawJob.teamInfo || '',
    relocationSupport: rawJob.relocationSupport,
    applicationLink: rawJob.applicationLink, // Include application link
    // Legacy fields for Job interface compatibility
    skills: [], // Not present in JOB_OPENINGS, set to empty array
    applicationDeadline: undefined,
    featured: false
  };
  
  // Convert all active jobs to proper JobData format
  const allJobs: JobData[] = jobs
    .filter(job => job.status === 'active' && isJobActive(job))
    .map(jobOpening => ({
      id: jobOpening.id,
      title: jobOpening.title,
      department: jobOpening.department,
      location: jobOpening.location,
      workMode: jobOpening.workMode || 'Remote',
      type: jobOpening.type as 'Full-time' | 'Part-time' | 'Contract' | 'Internship',
      experience: jobOpening.experience || 'Not specified',
      salary: jobOpening.salary,
      postedDate: jobOpening.postedDate || new Date().toISOString(),
      expirationDate: jobOpening.expirationDate,
      description: jobOpening.description,
      jobSummary: jobOpening.jobSummary || jobOpening.description,
      responsibilities: jobOpening.responsibilities || [],
      requirements: jobOpening.requirements || [],
      preferredQualifications: jobOpening.preferredQualifications || [],
      benefits: jobOpening.benefits || [],
      teamInfo: jobOpening.teamInfo || '',
      relocationSupport: jobOpening.relocationSupport,
      applicationLink: jobOpening.applicationLink,
      // Legacy fields for Job interface compatibility
      skills: [],
      applicationDeadline: undefined,
      featured: false
    }));
  
  return (
    <JobDetailTemplate 
      job={job} 
      allJobs={allJobs}
    />
  );
}